import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectAccountOnboarding, ConnectComponentsProvider } from '@stripe/react-connect-js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  ArrowLeft, Zap, Users, DollarSign, Loader2, Clock, FileText,
  Landmark, ShieldCheck, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL as BACKEND_URL } from '../config/api';

const EXPIRY_OPTIONS = [
  { value: 1, label: '1 hour' },
  { value: 4, label: '4 hours' },
  { value: 12, label: '12 hours' },
  { value: 24, label: '24 hours' },
  { value: 48, label: '48 hours' },
  { value: 72, label: '72 hours' },
];

// Stripe Embedded Onboarding — requirement filter.
// - `fields: 'currently_due'`     : only collect what Stripe considers due now (default).
// - `futureRequirements: 'omit'`  : don't preload additional forward-looking checks.
// - `requirements.exclude: [...]` : hide fields we ALREADY prefill server-side
//   during account creation (cashout_router.py). Excluding a field only hides
//   it from the UI — Stripe still needs the value, which is why every entry
//   below MUST match a value passed to `stripe.Account.create({...})`. Adding
//   an entry here without the matching prefill would leave the account unable
//   to satisfy KYC.
//
//   NOTE on `business_type`: this is a TOP-LEVEL requirement (not nested
//   under `business_profile.*`). We prefill `business_type: "individual"` in
//   `Account.create`, so excluding it here suppresses the "Select business
//   type / Confirm you're an individual" screen. Verified via Stripe docs:
//   https://docs.stripe.com/connect/supported-embedded-components/account-onboarding#requirement-restrictions
//
// - Frozen (Object.freeze + module-scope const) so <ConnectAccountOnboarding>
//   sees a stable reference and doesn't re-run its Stripe setter on every
//   parent re-render.
const ONBOARDING_COLLECTION_OPTIONS = Object.freeze({
  fields: 'currently_due',
  futureRequirements: 'omit',
  requirements: {
    exclude: [
      'business_type',
      'business_profile.mcc',
      'business_profile.url',
      'business_profile.name',
      'business_profile.product_description',
      'business_profile.support_email',
      'business_profile.support_url',
    ],
  },
});

// Notice banner shown ABOVE the create form when the host is not yet
// payout-ready. Form fields stay fully editable; this is purely informational
// so users know what the CTA at the bottom will do.
//
// Unified copy for not_started / in_progress / restricted — we focus on the
// outcome ("create your Quick Split") rather than Stripe terminology
// ("payouts"). Verifying is kept separate because it's not a setup action,
// it's a wait state.
const NOT_READY_NOTICE = {
  icon: Landmark,
  title: 'Connect your bank to create your Quick Split',
  body: 'Required to receive payments from guests.',
};
const NOTICE_COPY = {
  not_started: NOT_READY_NOTICE,
  in_progress: NOT_READY_NOTICE,
  restricted: NOT_READY_NOTICE,
  verifying: {
    icon: ShieldCheck,
    title: 'Verification in progress',
    body: "Stripe is verifying your details. This usually takes just a few minutes — we'll unlock Create Split as soon as you're approved.",
  },
};

// ===== Optional Venmo opt-in (inline, never blocks creation) =====
const VenmoOptIn = () => {
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState(null); // null = unknown/loading, '' = no handle, '<handle>' = saved
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/settings/profile`, { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const d = await res.json();
        if (cancelled) return;
        setSaved(d.venmo_username || '');
      } catch {
        if (!cancelled) setSaved('');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Already configured or still loading? Don't show the opt-in.
  if (saved === null || saved) return null;

  const handleSave = async () => {
    const payload = value.trim().replace(/^@/, '');
    if (!payload) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venmo_username: payload }),
      });
      if (res.ok) {
        setSaved(payload);
        toast.success('Venmo username saved');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save Venmo username');
      }
    } catch {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-sky-200 bg-sky-50/50 rounded-lg p-3" data-testid="qs-venmo-optin">
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 inline-flex items-center justify-center rounded bg-sky-100 text-sky-700 font-bold text-xs">V</span>
        <p className="text-sm font-medium text-gray-800">Let guests pay you through Venmo</p>
        <span className="text-xs text-gray-400">(optional)</span>
      </div>
      <p className="text-xs text-gray-500 mt-1 mb-2">Add your Venmo username.</p>
      <div className="flex gap-2">
        <Input
          type="text"
          placeholder="@yourhandle"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          className="h-9 text-sm flex-1"
          data-testid="qs-venmo-input"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleSave}
          disabled={saving || !value.trim()}
          data-testid="qs-venmo-save-btn"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
        </Button>
      </div>
    </div>
  );
};

const QuickSplitCreate = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillAmount = params.get('amount') || '';
  const prefillPeople = params.get('people') || '';

  // ===== Form state — persisted across onboarding because the parent
  // component does not unmount when we switch render branches. =====
  const [description, setDescription] = useState('');
  const [totalAmount, setTotalAmount] = useState(prefillAmount);
  const [numberOfPeople, setNumberOfPeople] = useState(prefillPeople);
  const [expiryHours, setExpiryHours] = useState(24);
  const [isCreating, setIsCreating] = useState(false);

  // ===== Payout readiness =====
  // readinessState: 'loading' | 'ready' | 'not_started' | 'in_progress' | 'verifying' | 'restricted' | 'error'
  const [readinessState, setReadinessState] = useState('loading');
  const [readinessError, setReadinessError] = useState('');

  // ===== Embedded onboarding =====
  // onboardingPhase: 'idle' | 'loading' | 'onboarding' | 'refreshing' | 'error'
  const [onboardingPhase, setOnboardingPhase] = useState('idle');
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [onboardingError, setOnboardingError] = useState('');

  // Initial readiness check on mount.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/quick-split/payout-readiness`, {
          credentials: 'include',
        });
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 401) {
            navigate('/login?next=/quick-split/create');
            return;
          }
          setReadinessState('error');
          setReadinessError('Could not check payout setup. Please try again.');
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setReadinessState(data.ready ? 'ready' : (data.state || 'not_started'));
      } catch {
        if (cancelled) return;
        setReadinessState('error');
        setReadinessError('Connection error. Please try again.');
      }
    };
    run();
    return () => { cancelled = true; };
  }, [navigate]);

  // Live refresh (used by Onboarding exit + verifying CTA).
  const refreshLive = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-split/payout-readiness/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setReadinessState('error');
        setReadinessError('Could not refresh payout setup. Please try again.');
        return;
      }
      const data = await res.json();
      setReadinessState(data.ready ? 'ready' : (data.state || 'not_started'));
    } catch {
      setReadinessState('error');
      setReadinessError('Connection error. Please try again.');
    }
  };

  const handleStartOnboarding = async () => {
    setOnboardingError('');
    setOnboardingPhase('loading');
    try {
      const onboardRes = await fetch(`${BACKEND_URL}/api/cashout/onboard`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!onboardRes.ok) {
        const err = await onboardRes.json().catch(() => ({}));
        setOnboardingError(typeof err.detail === 'string' ? err.detail : 'Setup failed. Please try again.');
        // Keep the user on the primer screen with the error inline
        // (qs-primer-error) so they can retry with one tap instead of
        // being dumped back to the create form.
        setOnboardingPhase('primer');
        return;
      }
      const onboardData = await onboardRes.json();

      // IMPORTANT: do NOT short-circuit when `onboarding_complete` is true.
      // The backend derives that flag from `acct.details_submitted`, which is
      // `true` even when the account is still verifying, has new requirements
      // due, or was put into a restricted state. Skipping the iframe in those
      // cases leaves the user with a no-op CTA (the symptom Ilir reported).
      // Stripe's embedded ConnectAccountOnboarding handles every state — it
      // will show the missing requirement, a verifying view, or a "done"
      // confirmation — so always mount it. The `onboardData` is intentionally
      // not used here; the call is still required because it provisions the
      // Connect account on the first attempt for a user who has none.
      void onboardData;

      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!publishableKey) {
        setOnboardingError('Stripe configuration missing. Please contact support.');
        setOnboardingPhase('primer');
        return;
      }

      const instance = loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () => {
          const res = await fetch(`${BACKEND_URL}/api/cashout/account-session`, {
            method: 'POST',
            credentials: 'include',
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to create session');
          }
          const data = await res.json();
          return data.client_secret;
        },
        appearance: { overlays: 'dialog', variables: { colorPrimary: '#2563eb' } },
      });

      setStripeConnectInstance(instance);
      setOnboardingPhase('onboarding');
    } catch (err) {
      console.error('Onboarding error:', err);
      setOnboardingError(err.message || 'Setup failed. Please try again.');
      setOnboardingPhase('primer');
    }
  };

  // Onboarding exit — always re-check live so we don't wait on the webhook.
  // Form values persist because they live on the parent component, not the
  // onboarding subtree.
  const handleOnboardingExit = async () => {
    setStripeConnectInstance(null);
    setOnboardingPhase('refreshing');
    await refreshLive();
    setOnboardingPhase('idle');
  };

  // ===== Form math =====
  const totalCents = Math.round(parseFloat(totalAmount || '0') * 100);
  const numPeople = parseInt(numberOfPeople || '0', 10);
  const perPersonCents = numPeople > 1 ? Math.floor(totalCents / numPeople) : 0;
  const isValid = totalCents > 0 && numPeople > 1 && numPeople <= 50;

  const handleCreate = async () => {
    if (!isValid) return;
    setIsCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-split/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          total_amount_cents: totalCents,
          number_of_people: numPeople,
          description: description.trim() || undefined,
          expiry_hours: expiryHours,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        navigate(`/quick-split/${data.id}/host`);
        return;
      }
      // Backend re-enforcement: if the client thought it was ready but the
      // server says otherwise, snap the UI back to the not-ready state.
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        const detail = body && body.detail;
        if (detail && detail.error === 'payout_setup_required') {
          setReadinessState(detail.state || 'not_started');
          toast.error('Finish payout setup before creating a Quick Split.');
          return;
        }
      }
      const err = await res.json().catch(() => ({}));
      toast.error(typeof err.detail === 'string' ? err.detail : 'Failed to create transaction');
    } catch {
      toast.error('Connection error');
    } finally {
      setIsCreating(false);
    }
  };

  // ===== Render: shared shell =====
  const header = (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="max-w-2xl mx-auto flex items-center space-x-4">
        <Button variant="ghost" size="icon" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')} data-testid="qs-create-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center space-x-2">
          <Zap className="w-5 h-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-900">Quick Split</h1>
        </div>
      </div>
    </header>
  );

  // ===== Render: initial readiness loading =====
  if (readinessState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50">
        {header}
        <div className="max-w-md mx-auto px-4 py-16 flex justify-center" data-testid="qs-readiness-loading">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  // ===== Render: primer (pre-Stripe intro; no Stripe calls yet) =====
  // User arrives here after clicking "Connect bank". Explains the outcome
  // in consumer-friendly language (no Stripe field terminology), then
  // user clicks "Start verification" to trigger the real Stripe API calls
  // and iframe mount. Also renders during onboardingPhase === 'loading'
  // so the button spinner stays in context instead of blanking out.
  if (onboardingPhase === 'primer' || onboardingPhase === 'loading') {
    const isPrimerLoading = onboardingPhase === 'loading';
    return (
      <div className="min-h-screen bg-gray-50">
        {header}
        <div className="max-w-md mx-auto px-4 py-8">
          <Card data-testid="qs-primer-card">
            <CardContent className="pt-8 pb-6 text-center space-y-5">
              <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
                <ShieldCheck className="w-7 h-7 text-blue-600" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-gray-900" data-testid="qs-primer-headline">
                  Receive payouts
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed" data-testid="qs-primer-body">
                  Verify your identity to receive payouts. Takes about 90 seconds — your Quick Split details are saved.
                </p>
                <p className="text-sm text-gray-500" data-testid="qs-primer-reassurance">
                  You&apos;ll only need to do this once.
                </p>
              </div>

              {onboardingError && (
                <p className="text-sm text-red-600" data-testid="qs-primer-error">
                  {onboardingError}
                </p>
              )}

              <Button
                onClick={handleStartOnboarding}
                disabled={isPrimerLoading}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold"
                data-testid="qs-primer-start-btn"
              >
                {isPrimerLoading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Opening…</>
                ) : (
                  <>Start verification →</>
                )}
              </Button>

              <p className="text-xs text-gray-500" data-testid="qs-primer-trust">
                Stripe securely handles verification and payouts
              </p>

              <p className="text-xs text-gray-400 text-center leading-relaxed" data-testid="qs-primer-legal">
                By starting verification, you agree to the{' '}
                <a
                  href="https://stripe.com/legal/ssa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-600"
                  data-testid="qs-primer-ssa-link"
                >
                  Stripe Services Agreement
                </a>{' '}
                and{' '}
                <a
                  href="https://stripe.com/connect-account/legal/full"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-600"
                  data-testid="qs-primer-connected-account-link"
                >
                  Stripe Connected Account Agreement
                </a>.
              </p>

              <button
                type="button"
                onClick={() => { setOnboardingPhase('idle'); setOnboardingError(''); }}
                disabled={isPrimerLoading}
                className="text-sm text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
                data-testid="qs-primer-back-btn"
              >
                Back
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ===== Render: embedded onboarding (replaces form view; form state preserved) =====
  if (onboardingPhase === 'onboarding' && stripeConnectInstance) {
    return (
      <div className="min-h-screen bg-gray-50">
        {header}
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Card data-testid="qs-onboarding-card">
            <CardHeader>
              <CardTitle className="text-lg">Set up payouts</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Complete a few steps so card, Apple Pay, and ACH payments deposit straight to your bank. Your split details are saved.
              </p>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg overflow-hidden" data-testid="qs-onboarding-embed">
                <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                  <ConnectAccountOnboarding
                    onExit={handleOnboardingExit}
                    collectionOptions={ONBOARDING_COLLECTION_OPTIONS}
                  />
                </ConnectComponentsProvider>
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOnboardingExit}
                  data-testid="qs-onboarding-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ===== Render: refreshing after onboarding =====
  if (onboardingPhase === 'refreshing') {
    return (
      <div className="min-h-screen bg-gray-50">
        {header}
        <div className="max-w-md mx-auto px-4 py-16 flex flex-col items-center" data-testid="qs-onboarding-refreshing">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500 mb-3" />
          <p className="text-sm text-gray-500">Checking your payout setup…</p>
        </div>
      </div>
    );
  }

  // ===== Render: create form (always interactive) =====
  const isReady = readinessState === 'ready';
  const isVerifying = readinessState === 'verifying';
  const showNotReadyUI = !isReady && readinessState !== 'error';
  const notice = NOTICE_COPY[readinessState];
  const NoticeIcon = notice ? notice.icon : null;

  let ctaLabel;
  if (isReady) {
    ctaLabel = isCreating ? 'Creating…' : 'Create Split';
  } else if (isVerifying) {
    ctaLabel = 'Check verification status';
  } else {
    // not_started / in_progress / restricted all share the same friendlier
    // outcome-focused copy.
    ctaLabel = 'Connect bank';
  }

  const handleCtaClick = () => {
    if (isReady) {
      handleCreate();
    } else if (isVerifying) {
      // Live re-check — webhook may have just landed.
      setOnboardingPhase('refreshing');
      refreshLive().then(() => setOnboardingPhase('idle'));
    } else {
      // Not-ready: show the primer screen first. Deliberate two-step so
      // users understand what they're about to see before Stripe mounts.
      // No API calls fire here — those happen when the primer's
      // "Start verification" button is tapped.
      setOnboardingError('');
      setOnboardingPhase('primer');
    }
  };

  // CTA disabled rules:
  // - Ready user: disabled until form is valid (existing behavior).
  // - Not ready: enabled (form validity not required to start onboarding).
  // - Loading states block all clicks.
  const ctaDisabled =
    isCreating ||
    onboardingPhase === 'loading' ||
    (isReady && !isValid);

  return (
    <div className="min-h-screen bg-gray-50">
      {header}

      <div className="max-w-md mx-auto px-4 py-8">
        <Card data-testid="qs-create-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold">New Quick Split</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 pt-4">

            {/* Payout-readiness notice (informational, never blocks input) */}
            {showNotReadyUI && notice && (
              <div
                className="flex items-start gap-3 p-3 border border-blue-200 bg-blue-50 rounded-lg"
                data-testid={`qs-payout-notice-${readinessState}`}
              >
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0 border border-blue-100">
                  <NoticeIcon className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-blue-900" data-testid="qs-notice-title">{notice.title}</p>
                  <p className="text-xs text-blue-800/80 leading-relaxed mt-0.5" data-testid="qs-notice-body">{notice.body}</p>
                </div>
              </div>
            )}

            {/* Transaction Name */}
            <div className="space-y-2">
              <Label htmlFor="description" className="flex items-center text-sm font-medium">
                <FileText className="w-4 h-4 mr-1 text-gray-400" />
                Transaction Name <span className="text-gray-400 ml-1">(optional)</span>
              </Label>
              <Input
                id="description"
                type="text"
                placeholder="e.g. Dinner at Nobu"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={100}
                className="h-12 text-base"
                data-testid="qs-description-input"
              />
            </div>

            {/* Total Amount */}
            <div className="space-y-2">
              <Label htmlFor="totalAmount" className="flex items-center text-sm font-medium">
                <DollarSign className="w-4 h-4 mr-1 text-gray-400" />
                Total Amount
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                <Input
                  id="totalAmount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  className="pl-8 h-12 text-lg"
                  data-testid="qs-total-amount-input"
                />
              </div>
            </div>

            {/* Total People (including host) */}
            <div className="space-y-2">
              <Label htmlFor="numPeople" className="flex items-center text-sm font-medium">
                <Users className="w-4 h-4 mr-1 text-gray-400" />
                Total people, including you
              </Label>
              <Input
                id="numPeople"
                type="number"
                min="2"
                max="50"
                placeholder="e.g. 4"
                value={numberOfPeople}
                onChange={(e) => setNumberOfPeople(e.target.value)}
                className="h-12 text-lg"
                data-testid="qs-num-people-input"
              />
              {numPeople > 1 && (
                <p className="text-xs text-gray-400 pl-1" data-testid="qs-num-people-helper">
                  You&apos;re included in this count — we&apos;ll collect from {numPeople - 1} other {numPeople - 1 === 1 ? 'person' : 'people'}.
                </p>
              )}
            </div>

            {/* Expiry */}
            <div className="space-y-2">
              <Label className="flex items-center text-sm font-medium">
                <Clock className="w-4 h-4 mr-1 text-gray-400" />
                Expires After
              </Label>
              <div className="grid grid-cols-3 gap-2" data-testid="qs-expiry-selector">
                {EXPIRY_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={expiryHours === opt.value ? 'default' : 'outline'}
                    className={expiryHours === opt.value ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
                    onClick={() => setExpiryHours(opt.value)}
                    data-testid={`qs-expiry-${opt.value}h`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Preview */}
            {isValid && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center" data-testid="qs-per-person-preview">
                <p className="text-xs text-blue-500 uppercase tracking-wide font-medium mb-1">Each person pays</p>
                <p className="text-3xl font-bold text-blue-700">${(perPersonCents / 100).toFixed(2)}</p>
                <p className="text-xs text-blue-400 mt-1">{numPeople} people total · You collect from {Math.max(0, numPeople - 1)} · ${(totalCents / 100).toFixed(2)} total · {expiryHours}h expiry</p>
              </div>
            )}

            {/* Optional Venmo opt-in — only when not payout-ready and no
                Venmo handle saved. Never blocks creation. */}
            {showNotReadyUI && <VenmoOptIn />}

            {/* Error from a previous onboarding attempt */}
            {onboardingError && (
              <p className="text-sm text-red-600" data-testid="qs-onboarding-error">
                {onboardingError}
              </p>
            )}
            {readinessState === 'error' && (
              <p className="text-sm text-red-600" data-testid="qs-readiness-error">
                {readinessError || 'Could not check payout setup.'}
              </p>
            )}

            <Button
              onClick={handleCtaClick}
              disabled={ctaDisabled}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold"
              data-testid={isReady ? 'qs-create-session-btn' : 'qs-connect-payouts-btn'}
            >
              {isCreating ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating...</>
              ) : isVerifying ? (
                <><RefreshCw className="w-4 h-4 mr-2" /> {ctaLabel}</>
              ) : (
                ctaLabel
              )}
            </Button>

            {/* Legal notice moved into the primer screen — it now sits
                directly above the "Start verification" button, which is
                the actual moment we provision the Stripe Connect account
                server-side and pre-accept the Connected Account Agreement
                on the user's behalf. Keeping the notice at the consent
                action instead of the form CTA. */}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuickSplitCreate;
